import {
  ChangeDetectionStrategy,
  Component,
  Input,
  Output,
  EventEmitter,
  ChangeDetectorRef,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DestroyRef } from '@angular/core';
import { CommunityItem, PostTypes } from '../../interfaces/community-item';
import { OwnerActionEventsEnum } from '../../interfaces/owner-action-event.enum';
import { PostService } from '../../services/api/post.service';
import { PostHeaderComponent } from './components/post-header/post-header.component';
import { PostTextComponent } from './components/post-text/post-text.component';
import { PostPlaylistComponent } from './components/post-playlist/post-playlist.component';
import { PostVideoComponent } from './components/post-video/post-video.component';
import { SocialMediaPostComponent } from './components/social-media-post/social-media-post.component';
import { RecommendationsComponent } from './components/recommendations/recommendations.component';
import { DialogManagerService } from '../../services/dialog/dialog-manager.service';
import { CommunityNavigationService } from '../../services/navigation/community-navigation.service';
import { ExternalPostNavigationService } from '../../services/navigation/external-post-navigation.service';
import { COMMUNITY_NAVIGATION_CONFIG } from '../../providers/community-navigation-config.provider';
import { ProfileLinkService, ProfileLinkContext } from '../../services/navigation/profile-link.service';
import { DataTestDirective, TargetUrl, User } from '@outlaw/core';
import { FeedType } from '../../interfaces/feed-type';
import { ProfileType } from '../../interfaces/profile-type';
import { PostActionsComponent, PostCommentComponent } from './components';
import { CommunityTabType } from '../../interfaces';
import { Router } from '@angular/router';
import { NgTemplateOutlet } from '@angular/common';
import { CommunityMobileEventsService } from '../../services';

@Component({
  selector: 'app-community-item',
  templateUrl: './community-item.component.html',
  styleUrls: ['./community-item.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    PostHeaderComponent,
    PostTextComponent,
    PostPlaylistComponent,
    PostVideoComponent,
    SocialMediaPostComponent,
    RecommendationsComponent,
    PostCommentComponent,
    NgTemplateOutlet,
    DataTestDirective,
    PostActionsComponent,
  ],
})
export class CommunityItemComponent {
  @Input() item: CommunityItem;
  @Input() currentUser: User;
  @Input() isMajiApp: boolean = false;
  @Input() variant: 'primary' | 'secondary' = 'primary';
  @Input() isGuest: boolean = false;
  @Input() feedType: FeedType = FeedType.user;
  @Input() profileType: ProfileType = 'user';
  @Input() linkContext: ProfileLinkContext = 'feed';
  @Input() openProfileInNewWindow?: boolean;
  @Input() openProfileLinksInSameWindow: boolean = true;
  @Input() currentTabId?: CommunityTabType;
  @Input() enableExternalProfileLink: boolean = false;
  @Input() isPostMaji: boolean = false;
  @Output() deleted = new EventEmitter<string>();
  @Output() shareLinkCopied = new EventEmitter<void>();
  @Output() watchMoreOpenDialog = new EventEmitter<void>();
  @Output() openGuestView = new EventEmitter<void>();
  @Output() saveToggled = new EventEmitter<CommunityItem>();

  loading = signal(false);
  readonly PostTypes = PostTypes;

  private postService = inject(PostService);
  private communityNavigationService = inject(CommunityNavigationService);
  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);
  private dialogManagerService = inject(DialogManagerService);
  private router = inject(Router);
  private mobileEventsService = inject(CommunityMobileEventsService);
  private externalPostNavService = inject(ExternalPostNavigationService);
  #navConfig = inject(COMMUNITY_NAVIGATION_CONFIG, { optional: true });
  private profileLinkService = inject(ProfileLinkService);

  get isFollowEvent(): boolean {
    return [PostTypes.followedPlaylist, PostTypes.followedUser, PostTypes.followedChannel].includes(
      this.item?.post_type?.slug
    );
  }

  get postType(): PostTypes {
    return this.item?.post_type?.slug === PostTypes.repost
      ? this.item?.content.community_repost?.original?.original_type
      : this.item?.post_type?.slug;
  }

  get isOwner(): boolean {
    return this.item.user.user_uuid === this.currentUser?.passport_uuid;
  }

  get isPostPinned(): boolean {
    return this.item.pinned_at !== null;
  }

  get profileLink(): string {
    if (this.enableExternalProfileLink) {
      return '';
    }
    return this.profileLinkService.buildProfileLink(this.profileType, this.item?.user?.username, this.linkContext);
  }

  get isRepost(): boolean {
    return this.item?.post_type?.slug === PostTypes.repost;
  }

  get hideRecommendations(): boolean {
    return !!this.#navConfig?.useExternalPostNavigation;
  }

  setLoading(isLoading: boolean): void {
    this.loading.set(isLoading);
    this.cdr.markForCheck();
  }

  ownerAction(action: OwnerActionEventsEnum): void {
    switch (action) {
      case OwnerActionEventsEnum.edit:
        this.editPost();
        break;
      case OwnerActionEventsEnum.delete:
        this.deletePost();
        break;
      case OwnerActionEventsEnum.pin:
        this.pinPost();
        break;
      case OwnerActionEventsEnum.unpin:
        this.unpinPost();
        break;
    }
  }

  openRecommendedUsersDialog() {
    this.dialogManagerService.openRecommendedByDialog({
      postUUID: this.item.post_uuid,
      isMajiApp: this.isMajiApp,
      postOwner: this.isRepost ? this.item.user : null,
    });
  }

  private editPost(): void {
    this.communityNavigationService.navigateToAdminPostEdit(this.item.post_uuid);
  }

  private deletePost(): void {
    this.postService
      .hideEvent(this.item.post_uuid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.deleted.emit(this.item.post_uuid);
        this.cdr.markForCheck();
      });
  }

  private pinPost(): void {
    this.postService
      .pinPost(this.item.post_uuid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.item = {
          ...this.item,
          pinned_at: new Date().getTime(),
        };
        this.cdr.markForCheck();
      });
  }

  private unpinPost(): void {
    this.postService
      .unpinPost(this.item.post_uuid)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.item = {
          ...this.item,
          pinned_at: null,
        };
        this.cdr.markForCheck();
      });
  }

  onNavigateToPost(target: TargetUrl = TargetUrl.sameWindow): void {
    const postId = this.item.post_uuid;
    const tabId = this.currentTabId;
    const returnUrl = this.router.url;
    const isMajiApp = this.isMajiApp;

    if (this.isPostMaji) {
      this.mobileEventsService.openPost(postId);
      return;
    }

    if (this.#navConfig?.useExternalPostNavigation) {
      const externalUrl = this.externalPostNavService.getExternalPostUrl(this.item);
      if (externalUrl) {
        window.open(externalUrl, '_blank');
      }
      return;
    }

    this.communityNavigationService.navigateToPostFromProfile(postId, target, tabId, returnUrl, isMajiApp);
  }
}
